import { AfterViewChecked, Component, OnInit, ViewEncapsulation } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core'
import { Keepalive } from '@ng-idle/keepalive'
import { EStorageType } from '@pichincha/angular-sdk/auth'
import { storage } from '@pichincha/angular-sdk/storage'
import { GoogleTagManagerService } from 'angular-google-tag-manager'
import { environment } from '../environments/environment'
import { JwtService } from './services/jwt-service/jwt.service'
import { LoaderService } from './services/loader-service/loader.service'
import { LoggedInService } from './services/logged-in-service/logged-in.service'
import { StepperService } from './services/stepper-service/stepper.service'
import { EVENT_NAMES } from './utils/events'
import { sendGtagEvent } from './utils/functions/analytic-event'
import { HEADERS } from './utils/head-tags'

declare global {
  const dataLayer: any
  interface Window {
    dataLayer: any
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnInit, AfterViewChecked {
  private loadedMetaTags: boolean = false
  productFlow: string = ''
  process: string = ''
  storage = storage({
    storageType: EStorageType.SESSION,
    secretKey: environment.storage.key,
  })

  showLoader: boolean = true
  public loaderSuscription

  showIdleModal = false
  timedOut = false
  public loggedInSuscription

  showStepper = false
  public stepperSuscription

  constructor(
    private idle: Idle,
    private router: Router,
    private keepalive: Keepalive,
    private loggedInService: LoggedInService,
    private loaderService: LoaderService,
    private stepperService: StepperService,
    private jwtService: JwtService,
    private gtmService: GoogleTagManagerService,
    private title: Title,
    private meta: Meta
  ) {
    this.timedOut = this.storage.get('onb:timedOut') || false

    this.loaderSuscription = this.loaderService.getLoader().subscribe(active => {
      this.showLoader = active
    })

    this.stepperSuscription = this.stepperService.getActualStep().subscribe(step => {
      this.showStepper = typeof step === 'number' && step > 0
    })

    /*
    Set session timeout timer for logged in pages
    */
    this.loggedInSuscription = this.loggedInService.getUserLoggedIn().subscribe(userLoggedIn => {
      if (userLoggedIn) this.idle.watch()
      else this.idle.stop()
    })

    /*
    Session timeout configuration
    */
    this.idle.setIdle(60 * 5) // 5 minutes for idle modal
    this.idle.setTimeout(60 * 5) // 5 minutes for timeout modal (10 minutes total)
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES)
    this.keepalive.interval(1)
    this.resetTimer()

    this.idle.onIdleEnd.subscribe(() => this.resetTimer())
    this.idle.onTimeout.subscribe(() => {
      this.setTimedOut(true)
      this.redirectToExpired()
    })
    this.idle.onIdleStart.subscribe(() => (this.showIdleModal = true))

    //Google tag manager initialize
    this.gtmService.pushTag({})
  }
  ngAfterViewChecked(): void {
    const flow = this.router.url.split('/')[1]
    const process = this.router.url.split('/')[2]?.split('?')[0]
    if (flow && !this.loadedMetaTags) {
      this.productFlow = flow
      this.process = process
      this.setHeadTags(flow)
      this.loadedMetaTags = flow !== 'none'
    }
  }

  public setHeadTags(flow: string) {
    this.title.setTitle(HEADERS[flow as keyof typeof HEADERS]['title'])
    this.meta.addTags(
      [
        {
          name: 'description',
          content: HEADERS[flow as keyof typeof HEADERS]['description'],
        },
        {
          name: 'keywords',
          content: HEADERS[flow as keyof typeof HEADERS]['keywords'],
        },
        {
          property: 'og:title',
          content: HEADERS[flow as keyof typeof HEADERS]['title'],
        },
        {
          name: 'og:title',
          property: 'og:title',
          content: HEADERS[flow as keyof typeof HEADERS]['title'],
        },
        {
          property: 'og:description',
          content: HEADERS[flow as keyof typeof HEADERS]['description'],
        },
        {
          property: 'og:image',
          content: HEADERS[flow as keyof typeof HEADERS]['image'],
        },
      ],
      true
    )
  }

  public expireSessionToken() {
    this.jwtService.disableJwt()
  }

  public resetTimer() {
    this.idle.watch()
    this.setTimedOut(false)
  }

  public setTimedOut(value: boolean) {
    this.timedOut = value
    this.storage.set('onb:timedOut', value)
  }

  public closeIdleModal() {
    this.showIdleModal = false
    this.resetTimer()
  }

  public redirectToExpired() {
    this.expireSessionToken()
    this.router.navigate(['/expirada'], { replaceUrl: true })
    this.closeIdleModal()
  }

  public redirectToHome() {
    this.expireSessionToken()
    this.router.navigate([this.productFlow, this.process], {
      replaceUrl: true,
    })
    this.closeIdleModal()
  }

  public handleInitAnalytics() {
    window.dataLayer = window.dataLayer || []
    function gtag(_text: string, _date: any) {
      dataLayer.push(arguments)
    }
    gtag('js', new Date())
    gtag('config', environment.google.analyticsId)
  }

  handleAnalyticsEvent = (e: Event) => {
    const { detail } = e as CustomEvent
    if (!detail) return
    sendGtagEvent(detail.eventName, detail.eventData)
  }

  ngOnInit() {
    this.handleInitAnalytics()
    window.addEventListener(EVENT_NAMES.analytics, this.handleAnalyticsEvent)
  }

  ngOnDestroy() {
    this.loggedInSuscription.unsubscribe()
    this.loaderSuscription.unsubscribe()
    this.stepperSuscription.unsubscribe()
  }
}
